import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  // Apple-style parallax transforms
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const headlineY = useTransform(scrollYProgress, [0, 0.5], ['0%', '-20%']);
  const subheadlineOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const ctaOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  // Video/Image effects on scroll
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const videoBlur = useTransform(scrollYProgress, [0, 0.6], [0, 8]);
  // Scroll indicator
  const scrollIndicatorOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  return <div ref={containerRef} className="relative h-[120vh] w-full overflow-hidden bg-[#1A1A1A]" data-id="element-129">
      {/* Cinematic Background - Mountain Home at Sunset */}
      <motion.div style={{
      scale: videoScale
    }} className="absolute inset-0 w-full h-full" data-id="element-130">
        <motion.div style={{
        filter: useTransform(videoBlur, v => `blur(${v}px)`)
      }} className="w-full h-full" data-id="element-131">
          <img src="https://cdn.magicpatterns.com/uploads/ooKBPUTFbp3oiYWvx53qb5/0N8A8863.jpg" alt="Covered deck with cedar gable ceiling, iron chandelier, and sweeping valley panorama" className="w-full h-full object-cover" data-id="element-132" />
        </motion.div>

        {/* Dark overlay for text readability on bright image */}
        <div className="absolute inset-0 bg-black/50" data-id="element-133" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-black/30 to-[#1A1A1A]/40" data-id="element-134" />
      </motion.div>

      {/* Content - Sticky within viewport */}
      <div className="sticky top-0 h-screen flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-20 md:pt-24" data-id="element-135">
        <motion.div style={{
        opacity: headlineOpacity,
        y: headlineY
      }} className="max-w-6xl mx-auto relative z-20" data-id="element-136">
          {/* Main Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-serif font-bold text-[#F5F3F0] leading-[1.05] mb-8 tracking-tight" data-id="element-137">
            Build Your
            <br data-id="element-138" />
            <span className="text-[#E8E4DF]" data-id="element-139">Vision</span>
            <br data-id="element-140" />
            <span className="italic font-semibold text-[#D4CFC8]" data-id="element-141">
              in the Mountains.
            </span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p style={{
          opacity: subheadlineOpacity
        }} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="text-base sm:text-lg md:text-xl text-[#E8E4DF]/90 max-w-2xl mx-auto mb-12 font-light leading-relaxed" data-id="element-142">
            Crafting bespoke mountain homes in Post Falls
            <br className="hidden sm:block" data-id="element-143" />
            and the greater Northern Idaho region.
          </motion.p>

          {/* CTA Button */}
          <motion.div style={{
          opacity: ctaOpacity
        }} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} data-id="element-144">
            <motion.button whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} className="group relative inline-flex items-center justify-center px-10 py-5 bg-[#A07656] text-white overflow-hidden transition-all duration-300 hover:bg-[#B8896A] hover:shadow-[0_0_50px_rgba(160,118,86,0.35)]" data-id="element-145">
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-146" />
              <span className="relative z-10 text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-147">
                Begin Your Journey
              </span>
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div style={{
        opacity: scrollIndicatorOpacity
      }} className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center" data-id="element-148">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2
        }} className="flex flex-col items-center gap-3" data-id="element-149">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#F5F3F0]/60 font-medium" data-id="element-150">
              Discover
            </span>
            <motion.div animate={{
            y: [0, 8, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-151">
              <ChevronDown className="w-5 h-5 text-[#F5F3F0]/60" data-id="element-152" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#1A1A1A] to-transparent z-10 pointer-events-none" data-id="element-153" />

      {/* Side vignette for cinematic feel */}
      <div className="absolute inset-0 pointer-events-none z-10" data-id="element-154">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(26,26,26,0.3)_100%)]" data-id="element-155" />
      </div>
    </div>;
}